import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements, AddressElement } from '@stripe/react-stripe-js';
import useSubmitOrder from '../../../hooks/cart/useSubmitOrder';
import useSendEmail from '../../../hooks/emails/useSendEmail';
import { db } from '../../../lib/firebase';
import { doc, updateDoc, increment } from 'firebase/firestore';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PK}`);
const SHIPPING_FEE = 9.99; // Added Shipping Fee Constant

const CheckoutForm = ({ cart, total, userId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { submitOrder, isSubmitting } = useSubmitOrder();
  const { sendEmail } = useSendEmail();

  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState('');

  // Calculate final total including shipping
  const finalTotal = total + SHIPPING_FEE;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;
    if (!cart || cart.length === 0) {
      setMessage('Your cart is empty.');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const addressElement = elements.getElement('address');
      const { complete, value } = await addressElement.getValue();

      if (!complete) {
        setMessage('Please complete your shipping information.');
        setIsProcessing(false);
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          receipt_email: email,
          shipping: {
            name: value.name,
            phone: value.phone,
            address: value.address,
          },
        },
      });

      if (stripeError) {
        setMessage(stripeError.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const orderData = {
          userId: userId || null,
          products: cart.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            sku: item.sku,
            imageUrl: item.imageUrl || '',
          })),
          shipping: {
            name: value.name,
            phone: value.phone,
            email,
            address: value.address,
          },
          paymentIntentId: paymentIntent.id,
          subtotal: total,           // Added for clarity
          shippingFee: SHIPPING_FEE, // Explicitly saved in DB
          totalAmount: finalTotal,   // The combined total
          status: 'pending',
          createdAt: new Date(),
        };

        const orderId = await submitOrder(orderData);

        if (orderId) {
          setMessage('Payment and order submitted successfully!');

          const receiptHtml = `
            <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 25px; border-radius: 12px; color: #333;">
              <h2 style="color: #16a34a; text-align: center; margin-bottom: 5px;">Order Confirmed!</h2>
              <p style="text-align: center; color: #666; margin-top: 0;">Order #${orderId.substring(0, 8).toUpperCase()}</p>
              
              <p>Hi <strong>${value.name}</strong>,</p>
              <p>Thank you for shopping with <strong>LootLayer</strong>. We've received your order and are getting it ready for shipment.</p>
              
              <div style="background: #f9fafb; padding: 20px; border-radius: 10px; margin: 25px 0;">
                <h4 style="margin: 0 0 15px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Order Summary</h4>
                <table style="width: 100%; border-collapse: collapse;">
                  ${cart.map(item => `
                    <tr>
                      <td style="padding: 10px 0; width: 60px;">
                        <img src="${item.imageUrl || 'https://via.placeholder.com/50'}" style="width: 50px; height: 50px; object-fit: cover; border-radius: 6px; border: 1px solid #eee;" />
                      </td>
                      <td style="padding: 10px 10px;">
                        <div style="font-weight: bold; font-size: 14px;">${item.name}</div>
                        <div style="font-size: 12px; color: #666;">Qty: ${item.quantity}</div>
                      </td>
                      <td style="padding: 10px 0; text-align: right; font-weight: bold;">
                        $${(item.price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  `).join('')}
                </table>
                <div style="margin-top: 15px; padding-top: 15px; border-top: 2px solid #e5e7eb; text-align: right; line-height: 1.6;">
                  <div style="font-size: 14px; color: #666;">Subtotal: $${total.toFixed(2)}</div>
                  <div style="font-size: 14px; color: #666;">Shipping: $${SHIPPING_FEE.toFixed(2)}</div>
                  <div style="font-size: 18px; margin-top: 5px;"><strong>Total Paid: $${finalTotal.toFixed(2)}</strong></div>
                </div>
              </div>
              <div style="text-align: center; margin-top: 40px; border-top: 1px solid #eee; padding-top: 20px;">
                <p style="font-size: 12px; color: #999;">&copy; 2026 LootLayer. All rights reserved.</p>
              </div>
            </div>
          `;

          await sendEmail({
            to: email,
            subject: `LootLayer Order Receipt #${orderId.substring(0, 6).toUpperCase()}`,
            html: receiptHtml,
          });

          // Stock update logic...
          for (const item of cart) {
            try {
              const productRef = doc(db, 'products', item.id);
              await updateDoc(productRef, { quantity: increment(-item.quantity) });
            } catch (err) { console.error(err); }
          }
        }
      }
    } catch (err) {
      setMessage('An unexpected error occurred.');
    }
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-md w-full mx-auto mt-10 p-6 bg-white rounded-2xl shadow-lg space-y-6">
      <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 mb-4">
          <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal:</span>
              <span>${total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600 font-medium">
              <span>Flat Rate Shipping:</span>
              <span>${SHIPPING_FEE.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-gray-900 mt-1 border-t border-purple-200 pt-1">
              <span>Total:</span>
              <span>${finalTotal.toFixed(2)}</span>
          </div>
      </div>
      {/* ... rest of the form inputs (Email, AddressElement, PaymentElement) ... */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800">Contact Information</h3>
        <input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none mb-3"
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800">Shipping Details</h3>
        <AddressElement
          options={{ 
            mode: 'shipping', 
            allowedCountries: ['CA', 'US'], 
            fields: { phone: 'always' },
            validation: { phone: { required: 'always' } }
          }}
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800">Payment Details</h3>
        <PaymentElement />
      </div>

      <button
        disabled={isProcessing || isSubmitting || !stripe}
        className={`w-full py-3 rounded-xl text-white font-semibold transition-all ${
          isProcessing || isSubmitting ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
        }`}
      >
        {isProcessing || isSubmitting ? 'Processing...' : `Pay $${finalTotal.toFixed(2)}`}
      </button>
      {message && <div className="text-red-600 text-sm mt-2 text-center font-medium">{message}</div>}
    </form>
  );
};

const Payment = ({ cart, total, userId }) => {
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    // IMPORTANT: Include Shipping in the PaymentIntent creation
    const amountToCharge = Math.round((total + SHIPPING_FEE) * 100);
    
    fetch(`${process.env.REACT_APP_STRIPE_API_URL}/create-payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: amountToCharge }),
    })
      .then(res => res.json())
      .then(data => setClientSecret(data.clientSecret))
      .catch(err => console.error('Error creating payment intent:', err));
  }, [total]);

  if (!clientSecret) return <p className="text-center mt-10 text-gray-600">Loading payment form...</p>;

  const options = {
    clientSecret,
    appearance: {
      theme: 'flat',
      variables: {
        colorPrimary: '#16a34a',
        borderRadius: '12px',
        fontFamily: 'Inter, sans-serif',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm cart={cart} total={total} userId={userId} />
    </Elements>
  );
};

export default Payment;